import { inject } from '@angular/core';
import {
  Router,
  type CanActivateFn,
  type ActivatedRouteSnapshot,
  type RouterStateSnapshot,
} from '@angular/router';
import { AuthStore } from '../store/auth.store';

/**
 * Guard for routes that require authentication (like switchboard, profile)
 * Redirects unauthenticated users to login
 */
export const authGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  // Load auth data from storage first
  authStore.loadFromStorage();

  if (!authStore.isLoggedIn()) {
    // User is not authenticated, redirect to login
    router.navigate(['/login']);
    return false;
  }

  const requiresReset = authStore.requiresPasswordReset();
  const targetUrl = state.url;

  if (requiresReset && !targetUrl.startsWith('/force-password-reset')) {
    router.navigate(['/force-password-reset']);
    return false;
  }

  if (!requiresReset && targetUrl.startsWith('/force-password-reset')) {
    router.navigate(['/switchboard']);
    return false;
  }

  // User is authenticated, allow access
  return true;
};

/**
 * Guard for public routes (like login, register)
 * Redirects authenticated users to switchboard
 */
export const publicGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  // Load auth data from storage first
  authStore.loadFromStorage();

  if (authStore.isLoggedIn()) {
    if (authStore.requiresPasswordReset()) {
      router.navigate(['/force-password-reset']);
    } else {
      router.navigate(['/switchboard']);
    }
    return false;
  }

  // User is not authenticated, allow access to public routes
  return true;
};
